import { Head, router } from "@inertiajs/react"
import { Check, FileDown, Loader2, X } from "lucide-react"
import { useState } from "react"

import { CorrespondenceThread } from "@/components/products/work_orders/correspondence-thread"
import { DetailsCard } from "@/components/products/work_orders/details-card"
import { DocumentsStrip } from "@/components/products/work_orders/documents-strip"
import { PodCard } from "@/components/products/work_orders/pod-card"
import {
  StatusChip,
  formatDate,
} from "@/components/products/work_orders/status-chip"
import type {
  CorrespondenceRow,
  DocumentRow,
  EditOptions,
  PodRow,
  ShopRecommendation,
  StatusChangeRow,
  TimelineStep,
  WorkOrderDetail,
  WorkOrderRow,
} from "@/components/products/work_orders/types"
import { WaitingBanner } from "@/components/products/work_orders/waiting-banner"
import { WorkOrderTimeline } from "@/components/products/work_orders/work-order-timeline"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Props {
  work_order: WorkOrderRow
  detail: WorkOrderDetail
  timeline: TimelineStep[]
  pod: { current: PodRow | null; history: PodRow[] }
  correspondences: CorrespondenceRow[]
  documents: DocumentRow[]
  history: StatusChangeRow[]
  options: EditOptions
  shop_recommendations: ShopRecommendation[]
  can_edit: boolean
}

/**
 * "I want to look at a rail car that's bad ordered and know exactly where it
 * stands." This page is that sentence.
 *
 * Reading order is deliberate: the banner answers who/how long/last-chased
 * before anything else is on screen, then the timeline says how it got here,
 * then the POD says when it leaves. Everything below that is evidence.
 */
export default function WorkOrderShow({
  work_order,
  detail,
  timeline,
  pod,
  correspondences,
  documents,
  history,
  options,
  shop_recommendations,
  can_edit,
}: Props) {
  const routes = useOrgRoutes()

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
    { title: "Work Orders", href: routes.workOrdersPath() },
    {
      title: work_order.car_mark_number ?? `#${work_order.id}`,
      href: routes.workOrderPath(work_order.id),
    },
  ]

  // Who the banner names: the party the clock is running against, by name where
  // we have one. "Waiting on Shop" is a category; "Waiting on Watco Coffeyville"
  // is something an operator can act on.
  const counterparty =
    work_order.waiting_on === "shop" || work_order.waiting_on === "shop_transit"
      ? work_order.shop_name
      : work_order.waiting_on === "lessor"
        ? (detail.car?.lessor_name ?? null)
        : null

  // Offer the one-click "request shop disposition" draft only while the
  // question is live: an open bad order whose disposition has not been asked
  // for yet, and which has no unsent lessor draft already waiting.
  const dispoRequested = timeline.some(
    (step) => step.column === "dispo_requested_at" && step.reached,
  )
  const hasOutboundLessorDraft = correspondences.some(
    (row) =>
      row.draft &&
      row.direction === "outbound" &&
      row.counterparty_type === "lessor",
  )
  const canRequestDispo =
    work_order.type === "BadOrder" &&
    work_order.open &&
    !dispoRequested &&
    !hasOutboundLessorDraft

  // The OTMA packet is a bad-order concept and only meaningful once a move is
  // being requested (otma_type set).
  const otmaType = detail.values.otma_type
  const showOtma = work_order.type === "BadOrder" && Boolean(otmaType)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head
        title={`${work_order.car_mark_number ?? "Work order"} — ${work_order.type_label}`}
      />

      <div className="space-y-5 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold">
              {work_order.car_mark_number ?? `Work order #${work_order.id}`}
            </h1>
            <Badge variant="outline">{work_order.type_label}</Badge>
            <StatusChip
              status_label={work_order.status_label}
              tone={work_order.tone}
            />
            {work_order.facility_name && (
              <span className="text-muted-foreground text-sm">
                {work_order.facility_name}
              </span>
            )}
          </div>
        </div>

        {work_order.review_status === "pending_review" && (
          <ReviewBar workOrder={work_order} canEdit={can_edit} />
        )}

        <WaitingBanner
          workOrder={work_order}
          counterpartyName={counterparty}
          correspondencesPath={routes.workOrderCorrespondencesPath(
            work_order.id,
          )}
          canEdit={can_edit}
        />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <div className="space-y-5 lg:col-span-2">
            <WorkOrderTimeline
              steps={timeline}
              updatePath={routes.workOrderPath(work_order.id)}
              canEdit={can_edit}
            />
            <CorrespondenceThread
              correspondences={correspondences}
              createPath={routes.workOrderCorrespondencesPath(work_order.id)}
              counterpartyTypes={options.counterparty_types}
              canEdit={can_edit}
              canRequestDispo={canRequestDispo}
            />
            <DocumentsStrip documents={documents} />
          </div>

          <div className="space-y-5">
            {showOtma && (
              <OtmaPackageCard
                documents={documents}
                otmaType={String(otmaType)}
                downloadPath={routes.otmaPackagePath(work_order.id)}
              />
            )}
            {shop_recommendations.length > 0 && (
              <ShopRecommendationsCard
                recommendations={shop_recommendations}
                updatePath={routes.workOrderPath(work_order.id)}
                canEdit={can_edit}
              />
            )}
            <PodCard
              current={pod.current}
              history={pod.history}
              createPath={routes.workOrderPodCommitmentsPath(work_order.id)}
              canEdit={can_edit}
            />
            <DetailsCard
              detail={detail}
              options={options}
              updatePath={routes.workOrderPath(work_order.id)}
              canEdit={can_edit}
            />
            <HistoryCard history={history} />
          </div>
        </div>
      </div>
    </AppLayout>
  )
}

/**
 * A work order the document pipeline PROPOSED. Nothing it says counts until an
 * operator accepts it, so the confirm/reject pair sits above the record itself.
 */
function ReviewBar({
  workOrder,
  canEdit,
}: {
  workOrder: WorkOrderRow
  canEdit: boolean
}) {
  const routes = useOrgRoutes()
  const [busy, setBusy] = useState<"confirm" | "reject" | null>(null)

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 dark:border-yellow-700/60 dark:bg-yellow-950/40">
      <p className="text-sm text-yellow-900 dark:text-yellow-200">
        This work order came from an extracted document and is pending review.
      </p>
      {canEdit && (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="border-green-500 text-green-700 hover:bg-green-50"
            disabled={busy !== null}
            onClick={() => {
              setBusy("confirm")
              router.post(
                routes.confirmWorkOrderPath(workOrder.id),
                {},
                { preserveScroll: true, onFinish: () => setBusy(null) },
              )
            }}
          >
            {busy === "confirm" ? (
              <Loader2 className="mr-1 size-4 animate-spin" />
            ) : (
              <Check className="mr-1 size-4" />
            )}
            Confirm
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-red-500 text-red-700 hover:bg-red-50"
            disabled={busy !== null}
            onClick={() => {
              if (!confirm("Reject this work order? This cannot be undone."))
                return
              setBusy("reject")
              router.post(
                routes.rejectWorkOrderPath(workOrder.id),
                {},
                { onFinish: () => setBusy(null) },
              )
            }}
          >
            {busy === "reject" ? (
              <Loader2 className="mr-1 size-4 animate-spin" />
            ) : (
              <X className="mr-1 size-4" />
            )}
            Reject
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * Where should this car go? Shown only while the question is still open — a
 * bad order with no shop picked yet (the server decides; see
 * WorkOrdersController#shop_recommendation_props).
 *
 * The REASONS are the point, not the rank. This is a rule-based score, so every
 * row can say exactly what earned it, and an operator who disagrees with the
 * ranking can still see the capability/geography/history facts and overrule it.
 * A number with no justification would just be a black box to ignore.
 */
function ShopRecommendationsCard({
  recommendations,
  updatePath,
  canEdit,
}: {
  recommendations: ShopRecommendation[]
  updatePath: string
  canEdit: boolean
}) {
  const [assigning, setAssigning] = useState<number | null>(null)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Recommended shops</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="space-y-3">
          {recommendations.map((rec, index) => (
            <li
              key={rec.shop_id}
              className="border-b pb-3 last:border-0 last:pb-0"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-xs tabular-nums">
                      {index + 1}
                    </span>
                    <span className="truncate text-sm font-medium">
                      {rec.company}
                    </span>
                  </div>
                  {(rec.city || rec.state) && (
                    <p className="text-muted-foreground mt-0.5 pl-5 text-xs">
                      {[rec.city, rec.state].filter(Boolean).join(", ")}
                    </p>
                  )}
                </div>
                {canEdit && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={assigning !== null}
                    onClick={() => {
                      setAssigning(rec.shop_id)
                      router.patch(
                        updatePath,
                        { work_order: { shop_id: rec.shop_id } },
                        {
                          preserveScroll: true,
                          onFinish: () => setAssigning(null),
                        },
                      )
                    }}
                  >
                    {assigning === rec.shop_id ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      "Assign"
                    )}
                  </Button>
                )}
              </div>
              <ul className="text-muted-foreground mt-1.5 space-y-0.5 pl-5 text-xs">
                {rec.reasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  )
}

/**
 * The FRA OTMA submission packet, for a bad order that has to move while still
 * defective. Shows which required documents are on file (green) or still
 * missing (red) before the packet can be submitted, and downloads the assembled
 * PDF. Missing documents don't block the download — they render as clearly
 * labelled placeholder pages inside the PDF (see OtmaPackage).
 */
const OTMA_REQUIRED_DOCS = [
  { docType: "home_shop_sticker", label: "Home shop sticker" },
  { docType: "defect_photo", label: "Defect photo" },
] as const

function OtmaPackageCard({
  documents,
  otmaType,
  downloadPath,
}: {
  documents: DocumentRow[]
  otmaType: string
  downloadPath: string
}) {
  const present = (docType: string) =>
    documents.some((doc) => doc.doc_type === docType)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">OTMA package</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-muted-foreground text-sm">
          FRA one-time movement approval — {otmaType.replace(/_/g, " ")}.
        </p>
        <ul className="space-y-1.5 text-sm">
          {OTMA_REQUIRED_DOCS.map((doc) => {
            const ok = present(doc.docType)
            return (
              <li key={doc.docType} className="flex items-center gap-2">
                {ok ? (
                  <Check className="size-4 shrink-0 text-green-600" />
                ) : (
                  <X className="size-4 shrink-0 text-red-600" />
                )}
                <span className={ok ? "" : "text-muted-foreground"}>
                  {doc.label}
                </span>
                {!ok && <span className="text-xs text-red-600">missing</span>}
              </li>
            )
          })}
        </ul>
        <Button
          size="sm"
          variant="outline"
          className="w-full"
          onClick={() => window.open(downloadPath, "_blank")}
        >
          <FileDown className="mr-1 size-4" />
          Generate OTMA PDF
        </Button>
      </CardContent>
    </Card>
  )
}

/** The audit trail — every status and waiting-on transition, newest first. */
function HistoryCard({ history }: { history: StatusChangeRow[] }) {
  if (history.length === 0) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle>History</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2 text-sm">
          {history.map((row) => (
            <li key={row.id} className="flex justify-between gap-3">
              <span className="min-w-0">
                <span className="text-muted-foreground capitalize">
                  {row.field_name.replace(/_/g, " ")}:{" "}
                </span>
                {row.from_value ?? "—"} → <strong>{row.to_value ?? "—"}</strong>
              </span>
              <span className="text-muted-foreground shrink-0 text-xs">
                {formatDate(row.created_at)}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}
